/**
 * Type definitions for credential validation
 */
export interface LoginValidationData {
    email: string;
    password: string;
  }
  
  export interface RegistrationValidationData extends LoginValidationData {
    name: string;
  }
  
  export interface UserValidationData extends RegistrationValidationData {
    title?: string;
  }
  
  /**
   * Individual field validators
   */
  export const validateEmail = (email: string): string | null => {
    if (!email) {
      return "E-Mail wird benötigt";
    }
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      return "Ungültige E-Mail Adresse";
    }
    return null;
  };
  
  export const validatePassword = (password: string, isRequired = true): string | null => {
    if (!password && isRequired) {
      return "Passwort wird benötigt";
    }
    if (password && password.length < 6) {
      return "Passwort muss mindestens 6 Zeichen lang sein";
    }
    return null;
  };
  
  export const validateName = (name: string): string | null => {
    if (!name) {
      return "Name wird benötigt";
    }
    if (name.length < 2) {
      return "Name muss mindestens 2 Zeichen lang sein";
    }
    if (name.length > 100) {
      return "Name darf maximal 100 Zeichen lang sein";
    }
    return null;
  };
  
  export const validateTitle = (title?: string): string | null => {
    if (title && title.length > 100) {
      return "Titel darf maximal 100 Zeichen lang sein";
    }
    return null;
  };
  
  /**
   * Composite validators for different forms
   */
  export const validateLoginCredentials = (credentials: LoginValidationData): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    const emailError = validateEmail(credentials.email);
    const passwordError = validatePassword(credentials.password);
    
    if (emailError) errors.email = emailError;
    if (passwordError) errors.password = passwordError;
    
    return errors;
  };
  
  export const validateRegistrationData = (data: RegistrationValidationData): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    const nameError = validateName(data.name);
    const emailError = validateEmail(data.email);
    const passwordError = validatePassword(data.password);
    
    if (nameError) errors.name = nameError;
    if (emailError) errors.email = emailError;
    if (passwordError) errors.password = passwordError;
    
    return errors;
  };
  
  export const validateUserCredentials = (data: UserValidationData, validatePasswordField = true): Record<string, string> => {
    const errors: Record<string, string> = {};
    
    const nameError = validateName(data.name);
    const emailError = validateEmail(data.email);
    const passwordError = validatePassword(data.password, validatePasswordField);
    const titleError = validateTitle(data.title);
    
    if (nameError) errors.name = nameError;
    if (emailError) errors.email = emailError;
    if (passwordError) errors.password = passwordError;
    if (titleError) errors.title = titleError;
    
    return errors;
  };
  
  /**
   * Validates if passwords match for registration/password change
   */
  export const validatePasswordMatch = (password: string, confirmPassword: string): string | null => {
    if (password !== confirmPassword) {
      return "Die Passwörter stimmen nicht überein";
    }
    return null;
  };
  
  /**
   * Helper to check if there are any validation errors
   */
  export const hasValidationErrors = (errors: Record<string, string | undefined>): boolean => {
    return Object.keys(errors).filter(key => errors[key] !== undefined).length > 0;
  };